import type { ReactNode } from 'react';
import { Link } from 'react-router';
import {
  LoginShell,
  Notice,
  OrganizationMark,
  PRODUCT_TAGLINES,
  ProductLockup,
  traceButtonClass,
} from '@traceio-org/auth-ui';
import type { ProductKey } from '@traceio-org/platform-contracts';
import { useBootstrap, useHub } from './context';
import { productQuery, usePageHeading, useSwitchAccount } from './hooks';

export const HUB_TAGLINE = 'One sign-in for every Trace product.';

/**
 * The page frame of every hub route. A customer domain shows the customer's mark (and "Customer
 * Portal" on a portal domain) with "Powered by Trace"; Trace's own hosts show the official product
 * lockup, or the master logo when no product is involved.
 */
export function HubFrame({
  productKey,
  headerActions,
  children,
}: {
  productKey?: ProductKey | null;
  /** Links for a signed-in person in the banner (the organization settings). */
  headerActions?: ReactNode;
  children: ReactNode;
}) {
  const { host } = useBootstrap();
  const { config } = useHub();
  if (host.kind === 'customer-employee' || host.kind === 'customer-portal') {
    const portal = host.kind === 'customer-portal';
    return (
      <LoginShell
        brand={<OrganizationMark branding={host.branding} surface="dark" assetBaseUrl={config.platformApiUrl} />}
        brandLabel={portal ? 'Customer Portal' : undefined}
        tagline={PRODUCT_TAGLINES[host.forcedProduct]}
        poweredByTrace
        accentColor={host.branding.accentColor}
        headerActions={headerActions}
      >
        {children}
      </LoginShell>
    );
  }
  const product = productKey ?? (host.kind === 'trace' ? host.forcedProduct : null);
  return (
    <LoginShell
      brand={product ? <ProductLockup productKey={product} surface="dark" /> : <ProductLockup lockup="trace" surface="dark" />}
      tagline={product ? PRODUCT_TAGLINES[product] : HUB_TAGLINE}
      headerActions={headerActions}
    >
      {children}
    </LoginShell>
  );
}

/** The page's only h1. Focus moves to it when the page (or its title) changes. */
export function PageHeading({ title, lead }: { title: string; lead?: ReactNode }) {
  const ref = usePageHeading(title);
  return (
    <div className="trace-auth-header">
      <h1 ref={ref} tabIndex={-1} className="trace-auth-title">
        {title}
      </h1>
      {lead ? <p className="trace-auth-lead">{lead}</p> : null}
    </div>
  );
}

/** Recoverable error after a failed navigation decision: never a guessed product list. */
export function DecideFailedNotice({ onRetry, busy = false }: { onRetry: () => void; busy?: boolean }) {
  return (
    <Notice tone="error" live="assertive" title="We could not finish signing you in.">
      <div className="trace-auth-stack">
        <p>Your sign-in worked, but Trace could not work out where to send you next.</p>
        <button type="button" className={traceButtonClass('primary', { block: true })} onClick={onRetry} disabled={busy}>
          Try again
        </button>
      </div>
    </Notice>
  );
}

/** "Use a different account" and "Sign out", shown wherever a signed-in person may be stuck. */
export function AccountActions({ productKey }: { productKey?: ProductKey | null | undefined }) {
  const switchAccount = useSwitchAccount(productKey);
  return (
    <div className="trace-auth-status__footer">
      <button type="button" className={traceButtonClass('link')} onClick={() => void switchAccount()}>
        Use a different account
      </button>
      <Link className="trace-auth-link" to={`/logout${productQuery(productKey)}`}>
        Sign out
      </Link>
    </div>
  );
}
